/**
 * PmAnalyticsDashboardComponent
 *
 * Displays analytics data for the Purchase Manager module.
 * Shows summary KPIs, funnel, supplier breakdown, daily trend,
 * user activity table, and event log.
 */
import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';

import { AuthService, EntityService } from '@app/core';
import { SUPPLIER_LABELS } from '../../analytics/constants/analytics.constants';
import { AnalyticsApiService } from '../../analytics/services/analytics-api.service';

interface FunnelStep {
    label: string;
    count: number;
    percent: number;
    color: string;
}

@Component({
    selector: 'app-pm-analytics-dashboard',
    templateUrl: './pm-analytics-dashboard.component.html',
    styleUrls: ['./pm-analytics-dashboard.component.scss'],
})
export class PmAnalyticsDashboardComponent implements OnInit, OnDestroy {
    private destroy$ = new Subject<void>();

    // Entity selection for Lead Admin
    entities: any[] = [];
    selectedEntity: any = null;
    showContent = false;
    entitiesLoading = false;

    // Current selected entity id for scoping analytics
    selectedEntityId = '';

    // Loading states
    summaryLoading = true;
    eventsLoading = false;
    sessionsLoading = false;
    pageLoading = false;

    // Date range
    dateRange: Date[] = [];
    startDate = '';
    endDate = '';

    // Active tab
    activeTab: 'summary' | 'events' | 'sessions' = 'summary';

    // Summary data
    totalEvents = 0;
    totalSessions = 0;
    totalUsers = 0;
    eventCounts: Record<string, number> = {};

    // Funnel
    funnelSteps: FunnelStep[] = [];

    // Supplier breakdown
    supplierBreakdown: Array<{
        supplier: string;
        label: string;
        totalEvents: number;
        searches: number;
        cartAdds: number;
        checkouts: number;
        clickToProceedToBuy: number;
        orderPlaced: number;
    }> = [];

    // Supplier donut data
    supplierDonut: Record<string, number> = {};

    // Daily trend
    dailyTrendSeries: any[] = [];
    dailyTrendCategories: string[] = [];

    // User activity
    userActivity: Array<{
        userId: string;
        userName: string;
        userEmail: string;
        totalEvents: number;
        eventBreakdown: Record<string, number>;
    }> = [];

    // Events table
    events: any[] = [];
    eventsPagination = { page: 1, pageSize: 20, total: 0, totalPages: 0 };
    eventTypeFilter = '';

    // Sessions table
    sessions: any[] = [];
    sessionsPagination = { page: 1, pageSize: 20, total: 0, totalPages: 0 };

    // Search keywords
    searchKeywords: Array<{ keyword: string; count: number; userName: string; userEmail: string; timestamp: string; supplier: string }> = [];
    searchKeywordsLoading = false;

    // Cart products
    cartProducts: Array<{ itemName: string; itemId: string; totalQty: number; unitPrice: number; supplier: string; userName: string; userEmail: string; timestamp: string; count: number }> = [];
    cartProductsLoading = false;

    // Event type options for filter
    eventTypeOptions = [
        { value: '', label: 'All Events' },
        { value: 'search', label: 'Search' },
        { value: 'search_result_view', label: 'Search Result View' },
        { value: 'search_item_select', label: 'Search Item Select' },
        { value: 'cart_add', label: 'Cart Add' },
        { value: 'cart_remove', label: 'Cart Remove' },
        { value: 'cart_quantity_change', label: 'Cart Quantity Change' },
        { value: 'cart_checkout_click', label: 'Cart Checkout Click' },
        { value: 'proceed_to_buy', label: 'Proceed to Buy' },
        { value: 'proceed_to_buy_success', label: 'Order Placed (Success)' },
        { value: 'proceed_to_buy_failure', label: 'Checkout Failed' },
        { value: 'create_po_open', label: 'Create PO Open' },
        { value: 'generate_po_click', label: 'Generate PO Click' },
        { value: 'session_start', label: 'Session Start' },
        { value: 'session_end', label: 'Session End' },
        { value: 'page_view', label: 'Page View' },
    ];

    private readonly funnelColors = [
        '#4CAF50', '#2196F3', '#FF9800', '#9C27B0', '#F44336', '#00BCD4'
    ];

    constructor(
        private analyticsApi: AnalyticsApiService,
        private authService: AuthService,
        private entityService: EntityService
    ) { }

    ngOnInit(): void {
        // Default: last 30 days
        const end = new Date();
        const start = new Date();
        start.setDate(start.getDate() - 30);
        this.dateRange = [start, end];
        this.applyDateRange();

        // Show content immediately - load aggregated data for all companies
        this.showContent = true;

        // Load entities for Lead Admin to select from
        this.loadEntities();

        // Load analytics data for all companies (aggregated)
        this.pageLoading = true;
        this.loadSummary();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    // ───────────── Entity Selection ──────────────

    /**
     * Load available entities for Lead Admin to select from
     */
    loadEntities(): void {
        this.entitiesLoading = true;

        // Get all entities from cache/localStorage
        const entities = this.entityService.getEntities();

        if (entities && Array.isArray(entities) && entities.length > 0) {
            this.entities = entities;
        } else {
            this.entities = [];
        }

        this.entitiesLoading = false;
    }

    /**
     * Handle entity selection from dropdown
     */
    onEntitySelect(entity: any): void {
        if (!entity) {
            return;
        }

        // Find the full entity object from the entities array
        const selectedEntityObj = this.entities.find(e => e.id === entity || e.id === entity.id);

        if (!selectedEntityObj) {
            return;
        }

        this.selectedEntity = selectedEntityObj;
        this.selectedEntityId = selectedEntityObj.id;
        this.showContent = true;

        // Reset all data
        this.events = [];
        this.sessions = [];
        this.searchKeywords = [];
        this.cartProducts = [];

        // Load analytics data for selected entity
        this.pageLoading = true;
        this.loadSummary();
    }

    // ───────────── Tab Actions ──────────────

    onTabChange(tab: 'summary' | 'events' | 'sessions'): void {
        this.activeTab = tab;
        if (tab === 'events' && this.events.length === 0) {
            this.pageLoading = true;
            this.loadEvents();
        }
        if (tab === 'sessions' && this.sessions.length === 0) {
            this.pageLoading = true;
            this.loadSessions();
        }
    }

    // ───────────── Date Range ──────────────

    onDateRangeChange(dates: Date[]): void {
        if (dates && dates.length === 2) {
            this.dateRange = dates;
            this.applyDateRange();
            this.refreshAll();
        }
    }

    private applyDateRange(): void {
        if (this.dateRange.length === 2) {
            this.startDate = this.dateRange[0].toISOString().split('T')[0];
            // Send end-of-day so the selected end date is fully included
            this.endDate = this.dateRange[1].toISOString().split('T')[0] + 'T23:59:59';
        }
    }

    private refreshAll(): void {
        this.pageLoading = true;
        this.loadSummary();
        if (this.activeTab === 'events') {
            this.eventsPagination.page = 1;
            this.loadEvents();
        }
        if (this.activeTab === 'sessions') {
            this.sessionsPagination.page = 1;
            this.loadSessions();
        }
    }

    private checkPageLoading(): void {
        this.pageLoading = this.summaryLoading || 
                          this.eventsLoading || 
                          this.sessionsLoading || 
                          this.searchKeywordsLoading || 
                          this.cartProductsLoading;
    }

    // ───────────── Data Loading ──────────────

    loadSummary(): void {
        this.summaryLoading = true;
        this.analyticsApi
            .getSummary({ startDate: this.startDate, endDate: this.endDate }, this.selectedEntityId)
            .pipe(
                takeUntil(this.destroy$),
                finalize(() => {
                    this.summaryLoading = false;
                    this.checkPageLoading();
                })
            )
            .subscribe(
                (data) => {
                    this.eventCounts = data.eventBreakdown || {};
                    this.totalEvents = data.totalEvents || Object.values(this.eventCounts).reduce((a, b) => a + b, 0);
                    this.userActivity = (data.activeUsers || []).map((u: any) => ({
                        userId: u._id || u.userId,
                        userName: u.userName,
                        userEmail: u.userEmail,
                        totalEvents: u.eventCount || u.totalEvents,
                        eventBreakdown: {
                            search: u.searchCount || 0,
                            cart_add: u.cartAddCount || 0,
                            cart_checkout_click: u.checkoutCount || 0,
                            order_placed: u.proceedToBuySuccess || u.proceedToBuyCount || 0, // In user context, this usually counts successes
                        }
                    }));
                    this.totalUsers = this.userActivity.length;
                    this.buildSupplierData(data.supplierBreakdown || []);
                    this.buildDailyTrend(data.dailyTrend || []);
                    this.buildFunnel(data.funnel);
                    this.totalSessions = this.eventCounts['session_start'] || 0;

                    // Also load search keywords and cart products
                    this.loadSearchKeywords();
                    this.loadCartProducts();
                },
                (err) => {
                    console.warn('[PM Analytics] Failed to load summary:', err);
                }
            );
    }

    loadEvents(): void {
        // Prevent duplicate calls if already loading
        if (this.eventsLoading) {
            return;
        }

        this.eventsLoading = true;
        this.analyticsApi
            .getEvents({
                page: this.eventsPagination.page,
                pageSize: this.eventsPagination.pageSize,
                eventType: this.eventTypeFilter || undefined,
                startDate: this.startDate,
                endDate: this.endDate,
            }, this.selectedEntityId)
            .pipe(
                takeUntil(this.destroy$),
                finalize(() => {
                    this.eventsLoading = false;
                    this.checkPageLoading();
                })
            )
            .subscribe(
                (data) => {
                    this.events = data.events || [];
                    if (data.pagination) {
                        // Update properties individually to avoid triggering unnecessary change detection
                        this.eventsPagination.page = data.pagination.page;
                        this.eventsPagination.pageSize = data.pagination.pageSize;
                        this.eventsPagination.total = data.pagination.total;
                        this.eventsPagination.totalPages = data.pagination.totalPages;
                    }
                },
                (err) => {
                    console.warn('[PM Analytics] Failed to load events:', err);
                }
            );
    }

    loadSessions(): void {
        // Prevent duplicate calls if already loading
        if (this.sessionsLoading) {
            return;
        }

        this.sessionsLoading = true;
        this.analyticsApi
            .getSessions({
                page: this.sessionsPagination.page,
                pageSize: this.sessionsPagination.pageSize,
                startDate: this.startDate,
                endDate: this.endDate,
            }, this.selectedEntityId)
            .pipe(
                takeUntil(this.destroy$),
                finalize(() => {
                    this.sessionsLoading = false;
                    this.checkPageLoading();
                })
            )
            .subscribe(
                (data) => {
                    this.sessions = data.sessions || [];
                    if (data.pagination) {
                        // Update properties individually to avoid triggering unnecessary change detection
                        this.sessionsPagination.page = data.pagination.page;
                        this.sessionsPagination.pageSize = data.pagination.pageSize;
                        this.sessionsPagination.total = data.pagination.total;
                        this.sessionsPagination.totalPages = data.pagination.totalPages;
                    }
                },
                (err) => {
                    console.warn('[PM Analytics] Failed to load sessions:', err);
                }
            );
    }

    // ───────────── Search Keywords & Cart Products ──────────────

    loadSearchKeywords(): void {
        this.searchKeywordsLoading = true;

        // Primary: fetch from batch-level searchKeywords summary
        this.analyticsApi
            .getSearchKeywords({
                startDate: this.startDate,
                endDate: this.endDate,
            }, this.selectedEntityId)
            .pipe(
                takeUntil(this.destroy$),
                finalize(() => {
                    this.searchKeywordsLoading = false;
                    this.checkPageLoading();
                })
            )
            .subscribe(
                (data) => {
                    const keywords = data?.searchKeywords || data || [];
                    if (Array.isArray(keywords) && keywords.length > 0) {
                        this.searchKeywords = keywords.map((k: any) => ({
                            keyword: k.keyword || '',
                            count: k.count || 1,
                            userName: k.userName || '-',
                            userEmail: k.userEmail || '-',
                            timestamp: k.timestamp || '',
                            supplier: k.supplier || '-',
                        })).sort((a: any, b: any) => b.count - a.count);
                    } else {
                        // Fallback: try individual events endpoint
                        this.loadSearchKeywordsFromEvents();
                    }
                },
                () => {
                    // Fallback: try individual events endpoint
                    this.loadSearchKeywordsFromEvents();
                }
            );
    }

    private loadSearchKeywordsFromEvents(): void {
        this.searchKeywordsLoading = true;
        this.analyticsApi
            .getEvents({
                eventType: 'search',
                startDate: this.startDate,
                endDate: this.endDate,
                pageSize: 5000,
                page: 1,
            }, this.selectedEntityId)
            .pipe(
                takeUntil(this.destroy$),
                finalize(() => (this.searchKeywordsLoading = false))
            )
            .subscribe(
                (data) => {
                    const events = data.events || [];
                    // Collect all search entries with details
                    const allSearches: Array<{ keyword: string; userName: string; userEmail: string; timestamp: string; supplier: string }> = [];
                    events.forEach((ev: any) => {
                        const kw = ev.data?.keyword || ev.keyword || '';
                        if (kw) {
                            allSearches.push({
                                keyword: kw,
                                userName: ev.userName || ev.userId || '-',
                                userEmail: ev.userEmail || '-',
                                timestamp: ev.timestamp || '',
                                supplier: ev.supplier || '-',
                            });
                        }
                    });

                    // Aggregate by keyword
                    const keywordMap = new Map<string, { keyword: string; count: number; userName: string; userEmail: string; timestamp: string; supplier: string }>();
                    allSearches.forEach((s) => {
                        const key = s.keyword.toLowerCase().trim();
                        if (keywordMap.has(key)) {
                            const existing = keywordMap.get(key)!;
                            existing.count++;
                            // Keep the latest timestamp
                            if (s.timestamp > existing.timestamp) {
                                existing.timestamp = s.timestamp;
                                existing.userName = s.userName;
                                existing.userEmail = s.userEmail;
                                existing.supplier = s.supplier;
                            }
                        } else {
                            keywordMap.set(key, { keyword: s.keyword, count: 1, userName: s.userName, userEmail: s.userEmail, timestamp: s.timestamp, supplier: s.supplier });
                        }
                    });

                    this.searchKeywords = Array.from(keywordMap.values()).sort((a, b) => b.count - a.count);
                },
                (err) => {
                    console.warn('[PM Analytics] Failed to load search keywords:', err);
                }
            );
    }

    loadCartProducts(): void {
        this.cartProductsLoading = true;

        // Primary: fetch from batch-level cartProducts summary
        this.analyticsApi
            .getCartProducts({
                startDate: this.startDate,
                endDate: this.endDate,
            }, this.selectedEntityId)
            .pipe(
                takeUntil(this.destroy$),
                finalize(() => {
                    this.cartProductsLoading = false;
                    this.checkPageLoading();
                })
            )
            .subscribe(
                (data) => {
                    const products = data?.cartProducts || data || [];
                    if (Array.isArray(products) && products.length > 0) {
                        this.cartProducts = products.map((p: any) => ({
                            itemName: p.itemName || '',
                            itemId: p.itemId || '',
                            totalQty: p.quantity || p.totalQty || 0,
                            unitPrice: p.unitPrice || 0,
                            supplier: p.supplier || '-',
                            userName: p.userName || '-',
                            userEmail: p.userEmail || '-',
                            timestamp: p.timestamp || '',
                            count: p.count || 1,
                        })).sort((a: any, b: any) => b.count - a.count);
                    } else {
                        // Fallback: try individual events endpoint
                        this.loadCartProductsFromEvents();
                    }
                },
                () => {
                    // Fallback: try individual events endpoint
                    this.loadCartProductsFromEvents();
                }
            );
    }

    private loadCartProductsFromEvents(): void {
        this.cartProductsLoading = true;
        this.analyticsApi
            .getEvents({
                eventType: 'cart_add',
                startDate: this.startDate,
                endDate: this.endDate,
                pageSize: 5000,
                page: 1,
            }, this.selectedEntityId)
            .pipe(
                takeUntil(this.destroy$),
                finalize(() => (this.cartProductsLoading = false))
            )
            .subscribe(
                (data) => {
                    const events = data.events || [];
                    // Aggregate by itemName
                    const productMap = new Map<string, { itemName: string; itemId: string; totalQty: number; unitPrice: number; supplier: string; userName: string; userEmail: string; timestamp: string; count: number }>();
                    events.forEach((ev: any) => {
                        const itemName = ev.data?.itemName || ev.itemName || '';
                        const itemId = ev.data?.itemId || ev.itemId || '';
                        const qty = ev.data?.quantity || ev.quantity || 0;
                        const price = ev.data?.unitPrice || ev.unitPrice || 0;
                        if (itemName) {
                            const key = (itemId || itemName).toLowerCase().trim();
                            if (productMap.has(key)) {
                                const existing = productMap.get(key)!;
                                existing.totalQty += qty;
                                existing.count++;
                                if (ev.timestamp > existing.timestamp) {
                                    existing.timestamp = ev.timestamp;
                                    existing.userName = ev.userName || ev.userId || '-';
                                    existing.userEmail = ev.userEmail || '-';
                                    existing.supplier = ev.supplier || '-';
                                }
                            } else {
                                productMap.set(key, {
                                    itemName,
                                    itemId,
                                    totalQty: qty,
                                    unitPrice: price,
                                    supplier: ev.supplier || '-',
                                    userName: ev.userName || ev.userId || '-',
                                    userEmail: ev.userEmail || '-',
                                    timestamp: ev.timestamp || '',
                                    count: 1,
                                });
                            }
                        }
                    });

                    this.cartProducts = Array.from(productMap.values()).sort((a, b) => b.count - a.count);
                },
                (err) => {
                    console.warn('[PM Analytics] Failed to load cart products:', err);
                }
            );
    }

    // ───────────── Data Transforms ──────────────

    private buildSupplierData(breakdown: any[]): void {
        this.supplierBreakdown = breakdown.map((s) => ({
            supplier: s._id || s.supplier,
            label: SUPPLIER_LABELS[s._id || s.supplier] || s._id || s.supplier || 'Unknown',
            totalEvents: s.totalEvents || 0,
            searches: s.searches || 0,
            cartAdds: s.cartAdds || 0,
            checkouts: s.checkouts || 0,
            clickToProceedToBuy: s.proceedToBuys || s.proceedToBuy || 0,
            orderPlaced: s.buySuccess || s.proceedToBuySuccess || 0,
        }));

        // Calculate totals for "all" row from other suppliers
        const allIndex = this.supplierBreakdown.findIndex((s) => s.supplier === 'all' || s.label === 'all');
        if (allIndex > -1) {
            const otherSuppliers = this.supplierBreakdown.filter((s) => s.supplier !== 'all' && s.label !== 'all');
            this.supplierBreakdown[allIndex].cartAdds = otherSuppliers.reduce((sum, s) => sum + s.cartAdds, 0);
            this.supplierBreakdown[allIndex].checkouts = otherSuppliers.reduce((sum, s) => sum + s.checkouts, 0);
            this.supplierBreakdown[allIndex].clickToProceedToBuy = otherSuppliers.reduce((sum, s) => sum + s.clickToProceedToBuy, 0);
            this.supplierBreakdown[allIndex].orderPlaced = otherSuppliers.reduce((sum, s) => sum + s.orderPlaced, 0);
        }

        this.supplierDonut = {};
        this.supplierBreakdown.forEach((s) => {
            this.supplierDonut[s.label] = s.totalEvents;
        });
    }

    private buildDailyTrend(trend: Array<{ _id?: string; date?: string; totalEvents?: number; count?: number }>): void {
        this.dailyTrendCategories = trend.map((d) => d._id || d.date || '');
        this.dailyTrendSeries = [
            {
                name: 'Events',
                data: trend.map((d) => d.totalEvents || d.count || 0),
            },
        ];
    }

    private buildFunnel(funnel: any): void {
        if (!funnel) {
            this.funnelSteps = [];
            return;
        }

        const steps = [
            { label: 'Searches', count: funnel.searches || 0 },
            { label: 'Search Results', count: funnel.results || funnel.searchResults || 0 },
            { label: 'Cart Adds', count: funnel.cartAdds || 0 },
            { label: 'Checkout Clicks', count: funnel.checkoutClicks || 0 },
            { label: 'Proceed to Buy', count: funnel.proceedToBuy || funnel.proceedToBuys || 0 },
            { label: 'Order Placed', count: funnel.buySuccess || funnel.proceedToBuySuccess || 0 },
            { label: 'PO Generated', count: funnel.poGenerated || 0 },
        ];

        const maxCount = steps[0].count || 1;
        this.funnelSteps = steps.map((s, i) => ({
            ...s,
            percent: Math.round((s.count / maxCount) * 100),
            color: this.funnelColors[i % this.funnelColors.length],
        }));
    }

    // ───────────── Pagination ──────────────

    onEventsPageChange(page: number): void {
        // Prevent unnecessary reload if already on this page or already loading
        if (this.eventsPagination.page === page || this.eventsLoading) {
            return;
        }
        this.eventsPagination.page = page;
        this.pageLoading = true;
        this.loadEvents();
    }

    onSessionsPageChange(page: number): void {
        // Prevent unnecessary reload if already on this page or already loading
        if (this.sessionsPagination.page === page || this.sessionsLoading) {
            return;
        }
        this.sessionsPagination.page = page;
        this.pageLoading = true;
        this.loadSessions();
    }

    // ───────────── Filter ──────────────

    onEventTypeFilterChange(): void {
        this.eventsPagination.page = 1;
        this.pageLoading = true;
        this.loadEvents();
    }

    // ───────────── Helpers ──────────────

    formatEventType(type: string): string {
        return (type || '')
            .split('_')
            .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
            .join(' ');
    }

    formatDate(iso: string): string {
        if (!iso) return '-';
        const d = new Date(iso);
        return d.toLocaleDateString('en-IN', {
            day: '2-digit', month: 'short', year: 'numeric',
            hour: '2-digit', minute: '2-digit',
        });
    }

    getTopEventTypes(): Array<{ type: string; count: number }> {
        return Object.entries(this.eventCounts)
            .sort(([, a], [, b]) => b - a)
            .slice(0, 10)
            .map(([type, count]) => ({ type, count }));
    }
}
